import type CSS from 'csstype'
import type { Theme } from 'theme-ui'
import type { PartialDeep, Simplify } from 'type-fest'

import type { BrandConfig } from '@jotta/types/Brand'
import { exactBaseBrandTheme } from '@jotta/ui/themes/base'

import type { BrandColorMap } from './ColorTypes'
import type { PartialVariantStyles, VariantStyles } from './VariantTypes'

export type ExactBaseBrandTheme = typeof exactBaseBrandTheme

/**
 * Get an array of keys for a top level property in the base theme
 * @example
 * ```
 * getKeysForBaseThemeProperty('button')
 * // Returns array of button types ['primary','secondary',...]
 * ```
 * @param key A key of the base theme style object
 */
export function getKeysForBaseThemeProperty<
  K extends keyof ExactBaseBrandTheme,
>(key: K) {
  return Object.keys(exactBaseBrandTheme[key]) as Array<
    keyof ExactBaseBrandTheme[K]
  >
}
export type InputValidState = 'NOT_VALIDATED' | 'VALID' | 'NOT_VALID'

export type BrandTheme = Simplify<
  Theme &
    VariantStyles & {
      branding: BrandConfig
      space: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ]
      fontSizes: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ]
      sizes: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ]
      radii: [number, number, number, number]
      colors: BrandColorMap<CSS.Property.Color>
    }
>

export type PartialBrandTheme = Simplify<
  Theme &
    PartialVariantStyles &
    Partial<{
      branding: PartialDeep<BrandConfig>
      space: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ]
      fontSizes: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ]
      sizes: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ]
      radii: [number, number, number, number]
      colors: Partial<BrandColorMap<CSS.Property.Color>>
    }>
>

export const brandTheme: BrandTheme =
  exactBaseBrandTheme as unknown as BrandTheme
